import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  EnterOutlined,
  FontColorsOutlined,
  ItalicOutlined,
  OrderedListOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { type Editor } from '@tiptap/react';
import type * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HEADING_LEVELS,
  type HeadingLevel,
  TEXT_SIZES,
} from '../extensions/TextSizeExtension.ts';
import { StyledSpace } from '../style/StyledSpace.tsx';
import { CiAlignJustify } from './buttons/IconsCustom.tsx';
import { RichTextLinkButtons } from './buttons/RichTextLinkButtons.tsx';
import { type MenuGroup } from './MenuGroup.tsx';
import { RichTextButton } from './RichTextMenuButton.tsx';
import { RichTextMenuDivider } from './RichTextMenuDivider.tsx';
import { RichTextMenuSelect } from './RichTextMenuSelect.tsx';

export interface IRichTextMenuBarProps {
  /** Istanza dell'editor TipTap a cui la barra applica i comandi. */
  editor: Editor | null;
  /** Gruppi di pulsanti da mostrare; se assenti usa il set di default. */
  menus?: MenuGroup[];
  /** Pulsanti aggiuntivi resi in coda alla barra. */
  extendedMenu?: (editor: Editor | null) => React.ReactNode;
  /** Colore di accento dei pulsanti. */
  color?: string;
}

/**
 * Barra dei comandi di formattazione per l'editor RichText (TipTap): mostra i
 * gruppi di pulsanti configurati e l'eventuale menu esteso.
 */
export function RichTextMenuBar(props: IRichTextMenuBarProps) {
  const { editor, menus } = props;
  const { t } = useTranslation();

  const extendedMenu = useMemo(
    () => props.extendedMenu?.(editor),
    [editor, props],
  );

  if (!editor) return null;

  const standard =
    !!menus?.find(m => m === 'default') || !menus || menus.length === 0;

  return (
    <StyledSpace wrap size={2}>
      {standard && (
        <>
          {/** Bold */}
          <RichTextButton
            icon={<BoldOutlined />}
            isActive={!!editor.isActive('bold')}
            title={t('inputs.rich-text-menu-bar.bold', 'Grassetto')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleBold().run()
            }
          />
          {/** Italic */}
          <RichTextButton
            icon={<ItalicOutlined />}
            isActive={!!editor.isActive('italic')}
            title={t('inputs.rich-text-menu-bar.italic', 'Corsivo')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleItalic().run()
            }
          />
          {/** Undeline */}
          <RichTextButton
            icon={<UnderlineOutlined />}
            isActive={!!editor.isActive('underline')}
            title={t('inputs.rich-text-menu-bar.underline', 'Sottolineato')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleUnderline().run()
            }
          />
          {/** Strike */}
          <RichTextButton
            icon={<StrikethroughOutlined />}
            isActive={!!editor.isActive('strike')}
            title={t('inputs.rich-text-menu-bar.strikethrough', 'Barrato')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleStrike().run()
            }
          />
        </>
      )}

      {/** Headings + Text Sizes */}
      {!!menus?.find(m => m === 'styles') && (
        <>
          <RichTextMenuDivider />
          <RichTextMenuSelect
            value={
              HEADING_LEVELS.find(level =>
                editor.isActive('heading', { level }),
              ) ??
              TEXT_SIZES.find(size =>
                editor.isActive('paragraph', { textSize: size }),
              ) ??
              0
            }
            disabled={!editor.isEditable}
            popupMatchSelectWidth={false}
            onChange={value => {
              if (value === 0) {
                editor
                  .chain()
                  .focus()
                  .setNode('paragraph', { textSize: null })
                  .run();
              } else if (typeof value === 'number') {
                editor
                  .chain()
                  .focus()
                  .setHeading({ level: value as HeadingLevel })
                  .run();
              } else {
                editor
                  .chain()
                  .focus()
                  .setNode('paragraph', { textSize: value })
                  .run();
              }
            }}
            options={[
              {
                label: t('inputs.rich-text-menu-bar.text-sizes', 'Dimensioni'),
                options: [
                  {
                    value: 'large',
                    label: t('inputs.rich-text-menu-bar.text-large', 'Grande'),
                  },
                  {
                    value: 0,
                    label: t(
                      'inputs.rich-text-menu-bar.text-normal',
                      'Normale',
                    ),
                  },
                  {
                    value: 'small',
                    label: t('inputs.rich-text-menu-bar.text-small', 'Piccolo'),
                  },
                  {
                    value: 'extra-small',
                    label: t(
                      'inputs.rich-text-menu-bar.text-extra-small',
                      'Molto piccolo',
                    ),
                  },
                ],
              },
              {
                label: t(
                  'inputs.rich-text-menu-bar.heading-group',
                  'Intestazioni',
                ),
                options: [
                  {
                    value: 1,
                    label: t('inputs.rich-text-menu-bar.heading-1', 'H1'),
                  },
                  {
                    value: 2,
                    label: t('inputs.rich-text-menu-bar.heading-2', 'H2'),
                  },
                  {
                    value: 3,
                    label: t('inputs.rich-text-menu-bar.heading-3', 'H3'),
                  },
                  {
                    value: 4,
                    label: t('inputs.rich-text-menu-bar.heading-4', 'H4'),
                  },
                  {
                    value: 5,
                    label: t('inputs.rich-text-menu-bar.heading-5', 'H5'),
                  },
                  {
                    value: 6,
                    label: t('inputs.rich-text-menu-bar.heading-6', 'H6'),
                  },
                ],
              },
            ]}
          />
        </>
      )}

      {/** Link */}
      {!!menus?.find(m => m === 'link') && (
        <RichTextLinkButtons editor={editor} />
      )}

      {!!menus?.find(m => m === 'title') && (
        <>
          <RichTextMenuDivider />
          {/** H2 | Titolo */}
          <RichTextButton
            isActive={!!editor.isActive('heading', { level: 2 })}
            title={t('inputs.rich-text-menu-bar.title', 'Titolo')}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            {t('inputs.rich-text-menu-bar.title', 'Titolo')}
          </RichTextButton>
        </>
      )}

      {!!menus?.find(m => m === 'align') && (
        <>
          <RichTextMenuDivider />
          <RichTextButton
            isActive={!!editor.isActive({ textAlign: 'left' })}
            title={t(
              'inputs.rich-text-menu-bar.align-left',
              'Allinea a sinistra',
            )}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().setTextAlign('left').run()
            }
          >
            <AlignLeftOutlined />
          </RichTextButton>
          <RichTextButton
            isActive={!!editor.isActive({ textAlign: 'center' })}
            title={t(
              'inputs.rich-text-menu-bar.align-center',
              'Allinea al centro',
            )}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().setTextAlign('center').run()
            }
          >
            <AlignCenterOutlined />
          </RichTextButton>
          <RichTextButton
            isActive={!!editor.isActive({ textAlign: 'right' })}
            title={t(
              'inputs.rich-text-menu-bar.align-right',
              'Allinea a destra',
            )}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().setTextAlign('right').run()
            }
          >
            <AlignRightOutlined />
          </RichTextButton>
          <RichTextButton
            isActive={!!editor.isActive({ textAlign: 'justify' })}
            title={t(
              'inputs.rich-text-menu-bar.justify-text',
              'Giustifica testo',
            )}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().setTextAlign('justify').run()
            }
          >
            <CiAlignJustify />
          </RichTextButton>
        </>
      )}

      {!!menus?.find(m => m === 'list') && (
        <>
          <RichTextMenuDivider />
          {/** Liste Non Ordinate */}
          <RichTextButton
            icon={<UnorderedListOutlined />}
            isActive={!!editor.isActive('bulletList')}
            title={t('inputs.rich-text-menu-bar.unordered-list', 'Elenco')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleBulletList().run()
            }
          />
          {/** Liste Ordinate */}
          <RichTextButton
            icon={<OrderedListOutlined />}
            isActive={!!editor.isActive('orderedList')}
            title={t(
              'inputs.rich-text-menu-bar.ordered-list',
              'Elenco ordinato',
            )}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            disabled={
              !editor.isEditable ||
              !editor.can().chain().focus().toggleOrderedList().run()
            }
          />
        </>
      )}
      {!!menus?.find(m => m === 'return') && (
        <>
          <RichTextMenuDivider />
          {/** Accapo */}
          <RichTextButton
            icon={<EnterOutlined />}
            title={t('inputs.rich-text-menu-bar.hard-break', 'Accapo')}
            onClick={() => {
              editor.chain().focus().setHardBreak().run();
            }}
            disabled={!editor.isEditable}
          />
        </>
      )}

      {!!menus?.find(m => m === 'color') && (
        <>
          <RichTextMenuDivider />
          {/** Colore */}
          <RichTextButton
            icon={<FontColorsOutlined />}
            isActive={!!editor.isActive('surveyColor')}
            title={t('inputs.rich-text-menu-bar.color', 'Colore')}
            onClick={() => {
              editor.chain().focus().toggleSurveyColor().run();
            }}
            disabled={!editor.isEditable}
          />
        </>
      )}

      {extendedMenu}
    </StyledSpace>
  );
}
